import { useEffect, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { dealApi, dealQueryKeys } from '@crm/entities/deal';

import type { TDeal, TDealDetails } from '@crm/entities/deal';

const DEAL_COMMENTS_POLLING_INTERVAL = 3000;

interface UseGetDealCommentsPollingParams {
    dealId: TDeal['id'];
    stateToken?: string;
}

export const useGetDealCommentsPolling = ({
    dealId,
    stateToken,
}: UseGetDealCommentsPollingParams) => {
    const queryClient = useQueryClient();
    const [currentStateToken, setCurrentStateToken] = useState(stateToken);

    useEffect(() => {
        setCurrentStateToken(stateToken);
    }, [stateToken]);

    const pollingQuery = useQuery({
        queryKey: dealQueryKeys.commentsPoll(dealId),
        queryFn: () => dealApi.pollDealComments({ dealId, stateToken: currentStateToken }),
        enabled: Boolean(dealId),
        refetchInterval: DEAL_COMMENTS_POLLING_INTERVAL,
        refetchOnMount: false,
        refetchIntervalInBackground: false,
    });

    useEffect(() => {
        const pollingData = pollingQuery.data;

        if (!pollingData) {
            return;
        }

        setCurrentStateToken(pollingData.stateToken);

        if (!pollingData.hasUpdates || !pollingData.comments) {
            return;
        }

        queryClient.setQueryData<TDealDetails>(dealQueryKeys.detail(dealId), (dealDetails) => {
            if (!dealDetails) {
                return dealDetails;
            }

            return {
                ...dealDetails,
                communicationData: {
                    ...dealDetails.communicationData,
                    stateToken: pollingData.stateToken,
                    comments: pollingData.comments,
                },
            };
        });
    }, [dealId, pollingQuery.data, queryClient]);

    return pollingQuery;
};
